/* eslint-disable max-lines-per-function, max-lines */
import { on as onEvent } from "common/utils/ui/event";
import { track as rudderstackTrack, extractTextFromLink } from "common/utils/rudderstack/tracker";
import { track, initMixpanelEvents } from "jiffy-analytics";
import { createDeliveryMonitor } from "common/utils/delivery-date-monitor";
import { api } from "jiffy-api";
import { waitForWindowVariable } from "common/utils/ui/wait_for";

// Initialize Delivery Date Monitor as early as possible to capture first network calls
(() => {
  try {
    const isDeliveryMonitorEnabled = document?.body?.dataset?.deliveryDateMonitor === "true";
    if (!isDeliveryMonitorEnabled) return;
    if (window.ddmInitialized) return;

    window.jiffyAnalytics = { track };
    createDeliveryMonitor({
      expiryHours: 6,
      enableMixpanel: true,
      mixpanelEventName: "delivery_date_discrepancy",
      logLevel: process.env.NODE_ENV === "production" ? "info" : "debug"
    });
    window.ddmInitialized = true;
  } catch (_e) {
    // no-op: body may not be ready yet; init() below will attempt again guarded
  }
})();

const $getOrderNumberContainer = () => document.querySelector(".js-order-number-container");

const $getOrderFeatureUsagesContainer = () =>
  document.querySelector(".js-order-feature-usages-container");

const getOrderNumber = () => $getOrderNumberContainer()?.dataset?.orderNumber;

const updateFeatureUsage = feature => {
  const orderNumber = getOrderNumber();
  if (!orderNumber) return;

  const allowedFeatures = JSON.parse(
    $getOrderFeatureUsagesContainer()?.dataset?.allowedFeatures || "[]"
  );
  if (!allowedFeatures.includes(feature)) return;

  const updateAllowedFeatures = param => {
    // The following code is for when the feature usage is getting updated without
    // navigating to other page, i.e. because of Ctrl + clicking on a link or choosing
    // "Open in new tab/window" on a link context menu.
    const $orderFeatureUsageContainer = $getOrderFeatureUsagesContainer();
    if (!$orderFeatureUsageContainer) return;

    let allowed;
    if (param) {
      const { allowed_features: updatableFeatures } = param;
      allowed = updatableFeatures;
    } else {
      allowed = allowedFeatures.filter(f => f !== feature);
    }

    $orderFeatureUsageContainer.dataset.allowedFeatures = JSON.stringify(allowed);
  };
  const notifyHoneybadger = error => {
    if (window.Honeybadger) {
      window.Honeybadger.notify("Order Feature Usage Updation failed", {
        params: {
          orderNumber,
          feature,
          error: {
            message: error.message,
            stack: error.stack,
            name: error.name
          }
        }
      });
    }
  };

  waitForWindowVariable("JiffyApi", () => {
    api.featureUsage
      .update({ order_number: orderNumber }, { feature })
      .then(updateAllowedFeatures)
      .catch(notifyHoneybadger);
  });
};

const initTopMenuEvents = () => {
  const links = document.querySelectorAll(".masthead-menu a");
  links.forEach(link => {
    link.addEventListener("click", () => {
      let mixpanelData;

      if (link.dataset?.mixpanelNavPayload) {
        mixpanelData = JSON.parse(link.dataset.mixpanelNavPayload);
      } else {
        const $title =
          link.closest(".js-masthead-s-nav-col")?.querySelector(".js-masthead-s-nav-col-title") ||
          link.closest(".js-masthead-menu-sub")?.querySelector(".js-masthead-menu-item-text");

        mixpanelData = {
          nav_location: "top",
          filter_name: link.textContent,
          parent: ($title && $title.textContent.trim()) || "",
          version: "v1"
        };
      }

      track("nav_clicked", mixpanelData);

      if (!link.dataset.mixpanelListen) {
        const $anyLineItem = document.querySelector(".js-item-details-tracker");
        const pageName = $anyLineItem?.dataset?.pageName;

        rudderstackTrack("Navigation Clicked", {
          destination: link.href,
          is_app: Boolean(window.MOBILE_APP),
          location: pageName || window.location.pathname,
          platform: window.MOBILE_APP_PLATFORM || "web",
          text: extractTextFromLink(link),
          type: "link",
          url: window.location.href,
          nav_type: "top_nav"
        });
      }

      updateFeatureUsage("top_nav");
    });
  });
};

const initSideMenuEvents = () => {
  const callback = ({ target: $target }) => {
    const $link = $target.closest("a");
    if (!$link) return;

    // Prefer explicit payload provided by markup
    let mixpanelData;
    if ($link.dataset?.mixpanelData) {
      try {
        mixpanelData = JSON.parse($link.dataset.mixpanelData);
      } catch (_e) {
        mixpanelData = null;
      }
    }

    // Fallback: derive from DOM
    if (!mixpanelData) {
      const facetContent = $link.closest('[data-testid="facet-content"]');
      const titleEl = facetContent?.previousElementSibling?.querySelector(
        '[data-testid="facet-title"]'
      );
      const parent = titleEl?.textContent?.trim() || "";
      const filterName =
        $link.dataset.gaEventLabel ||
        $link.textContent?.trim() ||
        $link.parentElement?.textContent?.trim();

      mixpanelData = {
        nav_location: "side",
        filter_name: filterName,
        parent,
        version: "v1"
      };
    }

    if (mixpanelData?.filter_name) {
      track("nav_clicked", mixpanelData);

      if (!$link.dataset.mixpanelListen) {
        const $anyLineItem = document.querySelector(".js-item-details-tracker");
        const pageName = $anyLineItem?.dataset?.pageName;

        rudderstackTrack("Navigation Clicked", {
          destination: $link.href,
          is_app: Boolean(window.MOBILE_APP),
          location: pageName || window.location.pathname,
          platform: window.MOBILE_APP_PLATFORM || "web",
          text: (mixpanelData.filter_name || "").trim(),
          type: "link",
          url: window.location.href,
          nav_type: "side_nav"
        });
      }

      updateFeatureUsage("side_nav");
    }
  };

  const dropdownCallback = () => {
    track("primary_material_filter_dropdown_opened", {});
  };

  const dropdownSelectCallback = ({ target: $target }) => {
    track("primary_material_filter_dropdown_selected", {
      material: $target.options[$target.selectedIndex].value
    });
  };

  onEvent("click", ".js-products-filter-link", callback, true);
  onEvent(
    "click",
    ".js-products-filter-desktop select, .js-products-filter-mobile select",
    dropdownCallback,
    true
  );
  onEvent(
    "change",
    ".js-products-filter-desktop select, .js-products-filter-mobile select",
    dropdownSelectCallback,
    true
  );
};

const initProductClickEvents = () => {
  const callback = ({ target: $target }) => {
    const $productItem = $target.closest(".js-products__item");
    const $lineItem = $target.closest(".js-item-details-tracker");
    const { gaList, gaPosition } = $productItem?.dataset ?? {};
    const {
      name,
      brand,
      category,
      compositionId,
      composition,
      pageName,
      fulfillmentGroup,
      clickContext,
      itemPosition,
      businessLine
    } = $lineItem.dataset;

    const productCardHeading = clickContext || gaList;
    const dbFeatureTracking = ["similar_products", "matching_products"];
    window.localStorage.setItem("pdpReferrerSection", productCardHeading);

    track("product_clicked", {
      version: "2",
      product: name,
      brand,
      product_category: category,
      spree_composition_id: compositionId,
      composition,
      fulfillment_group: fulfillmentGroup,
      page_name: pageName,
      click_context: productCardHeading || pageName,
      item_position: itemPosition || gaPosition,
      business_line: businessLine
    });

    const argument = productCardHeading?.toLowerCase()?.replace(" ", "_");
    if (dbFeatureTracking.includes(argument)) {
      updateFeatureUsage(argument);
    }
  };

  onEvent("click", ".js-product-item-trigger", callback, true);
};

const initCheckoutConfirmedEvents = () => {
  $(document).on("click", ".checkout-confirm-button__action, #purchase_button", button => {
    const orderTotal = document
      .querySelector("#checkout-order-total")
      .innerText.replace(/[^0-9.-]+/g, "");
    let paymentSource = "";

    if (button.currentTarget.className === "checkout-confirm-button__action") {
      paymentSource = "paypal";
    } else if (button.currentTarget.id === "purchase_button") {
      paymentSource = "default";
    }

    track("checkout_confirmed", {
      amount: Number(orderTotal),
      source_of_payment: paymentSource
    });
  });
};

const initCustomerEvents = () => {
  const element = document.querySelector(".alert-success, .flash-message__text--flash-success");
  if (element) {
    const welcomeMsg = element.innerHTML;
    const $oauthProviderMetaTag = document.head.querySelector('meta[property="oauthProviderUsed"]');
    const loginType = $oauthProviderMetaTag ? $oauthProviderMetaTag.content : "Email";
    const loginEventVersion = "2";
    const connectEventVersion = "0";

    if (welcomeMsg.includes("Welcome back")) {
      track("customer_logged_in", { type: loginType, version: loginEventVersion });
    } else if (welcomeMsg.endsWith("is disconnected")) {
      track("social_account_disconnected", { type: loginType, version: connectEventVersion });
    } else if (welcomeMsg.endsWith("is connected")) {
      track("social_account_connected", { type: loginType, version: connectEventVersion });
    }
  }
};

const initResetMixpanelWhenLogout = () => {
  $(document).on("click", ".user-logout-btn", () => {
    if (window.mixpanel) {
      window.mixpanel.reset();
    }
  });
};

const initPlpPaginationEvents = () => {
  const $links = Array.from(document.querySelectorAll(".js-plp-search-pagination a"));
  $links.forEach($link => {
    $link.addEventListener("click", () => {
      track("plp_pagination_link_clicked", {
        href: $link.href,
        page: $link.textContent
      });
    });
  });
};

const initTrackProductTilesShip = () => {
  const callback = ({ target: $target }) => {
    const {
      brand,
      category,
      compositionId,
      composition,
      pageName,
      fulfillmentGroup,
      businessLine
    } = $target.dataset;
    const $productItem = $target.closest(".js-products__item");
    const { gaPosition, gaList } = $productItem?.dataset || {};

    track("fulfillment_plp_filter_clicked", {
      brand,
      product_category: category,
      spree_composition_id: compositionId,
      composition,
      fulfillment_group: fulfillmentGroup,
      page_name: pageName,
      click_context: gaList,
      item_position: gaPosition,
      business_line: businessLine
    });
  };

  onEvent("click", ".js-ships-tooltip", callback, true);
};

const trackProductGridScroll = (elementClass, direction) => {
  const callback = ({ target: $target }) => {
    const { mpListHeader: name } = $target.dataset;
    track("subsection_carousel_clicked", {
      direction,
      name
    });
  };

  onEvent("click", elementClass, callback, true);
};

const initTrackProductGridScroll = () => {
  trackProductGridScroll(".js-product-grid-scroll-left", "left");
  trackProductGridScroll(".js-product-grid-scroll-right", "right");
};

const initTrackUseThisAddressBtn = () => {
  const callback = () => {
    track("use_this_address_button_clicked");
  };

  onEvent("click", ".js-checkout-guest-address-button", callback, true);
};

const initTrackCheckoutUseShippingAddress = () => {
  const callback = ({ target: $target }) => {
    track("same_as_shipping_address_clicked", {
      action: $target.checked ? "checked" : "unchecked"
    });
  };

  onEvent("change", ".js-order-use-shipping-checkbox", callback, true);
};

const init = () => {
  initMixpanelEvents();
  // Enable/disable Delivery Date Monitor based on body data attribute
  const isDeliveryMonitorEnabled = document?.body?.dataset?.deliveryDateMonitor === "true";

  if (isDeliveryMonitorEnabled && !window.ddmInitialized) {
    // Initialize delivery date monitor with all API endpoints (guarded)
    window.jiffyAnalytics = { track };
    createDeliveryMonitor({
      expiryHours: 6,
      enableMixpanel: true,
      mixpanelEventName: "delivery_date_discrepancy",
      logLevel: process.env.NODE_ENV === "production" ? "info" : "debug"
    });
    window.ddmInitialized = true;
  }
  initTopMenuEvents();
  initSideMenuEvents();
  initProductClickEvents();
  initCheckoutConfirmedEvents();
  initCustomerEvents();
  initResetMixpanelWhenLogout();
  initPlpPaginationEvents();
  initTrackProductTilesShip();
  initTrackProductGridScroll();
  initTrackUseThisAddressBtn();
  initTrackCheckoutUseShippingAddress();
};

document.addEventListener("DOMContentLoaded", init);
